import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';

// ── HELPERS ───────────────────────────────────────────────────────────────────
function fmtMoney(n) { return `$${(n || 0).toFixed(2)}`; }
function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TIER_RANK = { starter: 0, growth: 1, pro: 2 };
function tierAtLeast(tier, required) {
  return (TIER_RANK[tier] ?? 0) >= (TIER_RANK[required] ?? 0);
}

// ── SHARED UI ─────────────────────────────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '1.4rem', ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.2rem' }}>
      {children}
    </h2>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <Card>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>{label}</div>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.9rem', fontWeight: 900, color: accent ? 'var(--orange)' : 'var(--ink)', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: '0.4rem' }}>{sub}</div>}
    </Card>
  );
}

// ── LOCKED SECTION ────────────────────────────────────────────────────────────
function LockedSection({ requiredTier, children }) {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.5 }}>
        {children}
      </div>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(250,246,240,0.85)', borderRadius: 12,
      }}>
        <div style={{ textAlign: 'center', padding: '1.5rem' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.6rem' }}>🔒</div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1rem', fontWeight: 700, marginBottom: '0.4rem', textTransform: 'capitalize' }}>
            {requiredTier} plan required
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '1rem', maxWidth: 260 }}>
            Upgrade to {requiredTier} to unlock this analytics section.
          </p>
          <a href="mailto:support@tablechat-ai.com?subject=Upgrade my plan"
            style={{ background: 'var(--orange)', color: 'white', padding: '0.6rem 1.4rem', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem' }}>
            Upgrade plan
          </a>
        </div>
      </div>
    </div>
  );
}

// ── CUSTOM TOOLTIP ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label, money }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.82rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>
          {p.name}: {money ? fmtMoney(p.value) : p.value}
        </div>
      ))}
    </div>
  );
}

// ── FUNNEL BAR ────────────────────────────────────────────────────────────────
function FunnelRow({ label, value, max, color, pct, sub }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>{value.toLocaleString()}{sub ? ` · ${sub}` : ''}</span>
      </div>
      <div style={{ height: 10, background: 'var(--warm)', borderRadius: 5 }}>
        <div style={{ height: 10, background: color, borderRadius: 5, width: `${max > 0 ? (value / max) * 100 : 0}%`, transition: 'width 0.5s' }} />
      </div>
      {pct !== undefined && (
        <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: '0.2rem' }}>{pct}% of viewers</div>
      )}
    </div>
  );
}

// ── MAIN ANALYTICS PAGE ───────────────────────────────────────────────────────
export default function Analytics() {
  const { restaurant } = useAuth();
  const [tier, setTier]               = useState('starter');
  const [loading, setLoading]         = useState(true);
  const [dateRange, setDateRange]     = useState(7);
  const [orders, setOrders]           = useState([]);
  const [reservations, setReservations] = useState([]);
  const [billing, setBilling]         = useState(null);
  const [discovery, setDiscovery]     = useState(null);
  const [gmv, setGmv]                 = useState(null);

  useEffect(() => {
    if (!restaurant?.id) return;
    loadAll();
  }, [restaurant?.id, dateRange]);

  // Dish intelligence -- a separate fetch from loadAll() above, since
  // api/portal/dish-intelligence.js computes across ALL orders/reviews,
  // not limited to the selected date range like the rest of this page.
  const [dishIntel, setDishIntel]           = useState([]);
  const [dishIntelLoading, setDishIntelLoading] = useState(true);

  useEffect(() => {
    if (!restaurant?.id) return;
    (async () => {
      setDishIntelLoading(true);
      const data = await portalFetch('/dish-intelligence');
      setDishIntel(data?.dishes || []);
      setDishIntelLoading(false);
    })();
  }, [restaurant?.id]);

  const loadAll = async () => {
    setLoading(true);
    const data = await portalFetch(`/analytics?days=${dateRange}`);
    if (data) {
      setOrders(data.orders || []);
      setReservations(data.reservations || []);
      setDiscovery(data.discovery || null);
      setGmv(data.gmv || null);
      if (data.billing) {
        setBilling(data.billing);
        setTier(data.billing.service_tier || 'starter');
      }
    }
    setLoading(false);
  };

  // ── DERIVED METRICS ───────────────────────────────────────────────────────────
  const paidOrders     = orders.filter(o => o.payment_status === 'paid');
  const totalRevenue   = paidOrders.reduce((s, o) => s + (o.total || 0), 0);
  const avgOrderValue  = paidOrders.length > 0 ? totalRevenue / paidOrders.length : 0;
  const confirmedRes   = reservations.filter(r => r.status !== 'cancelled');
  const totalCovers    = confirmedRes.reduce((s, r) => s + (r.party_size || 0), 0);

  const revenueByDay = (() => {
    const map = {};
    for (let i = dateRange - 1; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      map[key] = { date: fmtDate(key), revenue: 0, orders: 0 };
    }
    paidOrders.forEach(o => {
      const key = o.created_at.split('T')[0];
      if (map[key]) { map[key].revenue += o.total || 0; map[key].orders += 1; }
    });
    return Object.values(map);
  })();

  const topDishes = (() => {
    const map = {};
    orders.forEach(o => {
      (o.order_items || []).forEach(item => {
        const name = item.dish_name || item.name;
        if (!map[name]) map[name] = { name, qty: 0, revenue: 0 };
        map[name].qty     += item.quantity || 1;
        map[name].revenue += (item.dish_price || 0) * (item.quantity || 1);
      });
    });
    return Object.values(map).sort((a, b) => b.qty - a.qty).slice(0, 10);
  })();

  const uniqueCustomers = (() => {
    const seen = new Set();
    orders.forEach(o => { const k = o.customer_email || o.customer_phone; if (k) seen.add(k); });
    return seen.size;
  })();

  const fulfillmentMap = {};
  orders.forEach(o => {
    const f = o.fulfillment_method || 'pickup';
    fulfillmentMap[f] = (fulfillmentMap[f] || 0) + 1;
  });
  const fulfillmentData = Object.entries(fulfillmentMap).map(([name, value]) => ({ name, value }));

  const resByDay = (() => {
    const map = {};
    for (let i = dateRange - 1; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      map[key] = { date: fmtDate(key), reservations: 0, covers: 0 };
    }
    reservations.forEach(r => {
      const key = r.created_at.split('T')[0];
      if (map[key]) { map[key].reservations += 1; map[key].covers += r.party_size || 0; }
    });
    return Object.values(map);
  })();

  const byDayOfWeek = (() => {
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const map = {};
    days.forEach(d => { map[d] = { day: d, revenue: 0, orders: 0 }; });
    paidOrders.forEach(o => {
      const d = days[new Date(o.created_at).getDay()];
      map[d].revenue += o.total || 0;
      map[d].orders  += 1;
    });
    return Object.values(map);
  })();

  const maxRevDay = [...byDayOfWeek].sort((a, b) => b.revenue - a.revenue)[0];

  // Discovery views by day formatted for chart
  const discoveryByDay = (discovery?.viewsByDay || []).map(d => ({
    ...d,
    date: fmtDate(d.date),
  }));

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 32, height: 32, border: '2px solid var(--border)', borderTopColor: 'var(--orange)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 0.8rem' }}/>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900 }}>Analytics</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>
            {restaurant?.name} · <span style={{ textTransform: 'capitalize', fontWeight: 600, color: 'var(--orange)' }}>{tier}</span> plan
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[[7, 'Last 7 days'], [30, 'Last 30 days'], [90, 'Last 90 days']].map(([days, label]) => (
            <button key={days} onClick={() => setDateRange(days)}
              style={{ padding: '0.5rem 1rem', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer',
                background: dateRange === days ? 'var(--orange)' : 'white',
                color: dateRange === days ? 'white' : 'var(--mid)',
                border: dateRange === days ? '1px solid var(--orange)' : '1px solid var(--border)' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── STARTER: KEY METRICS ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard label="Total revenue"    value={fmtMoney(totalRevenue)} sub={`${paidOrders.length} paid orders`} accent />
        <StatCard label="Avg order value"  value={fmtMoney(avgOrderValue)} />
        <StatCard label="Reservations"     value={confirmedRes.length} sub={`${totalCovers} total covers`} />
        <StatCard label="Unique customers" value={uniqueCustomers} />
      </div>

      {/* ── STARTER: TOP DISHES ──────────────────────────────────────────────── */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <SectionTitle>Top dishes — last {dateRange} days</SectionTitle>
        {topDishes.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No order data yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {topDishes.slice(0, 5).map((dish, i) => {
              const maxQty = topDishes[0].qty;
              return (
                <div key={dish.name} style={{ display: 'grid', gridTemplateColumns: '24px 1fr 120px 80px', gap: '0.8rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: i === 0 ? 'var(--orange)' : 'var(--muted)' }}>#{i + 1}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{dish.name}</div>
                    <div style={{ height: 4, background: 'var(--warm)', borderRadius: 2, marginTop: 4 }}>
                      <div style={{ height: 4, background: i === 0 ? 'var(--orange)' : 'var(--border)', borderRadius: 2, width: `${(dish.qty / maxQty) * 100}%`, transition: 'width 0.5s' }}/>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.82rem', color: 'var(--muted)', textAlign: 'right' }}>{dish.qty} orders</span>
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--orange)', textAlign: 'right' }}>{fmtMoney(dish.revenue)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── DISCOVERY ANALYTICS ──────────────────────────────────────────────── */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <SectionTitle>📭 Discovery analytics</SectionTitle>

        {!discovery || discovery.views === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No discovery data for this period yet.</p>
        ) : (
          <>
            {/* Top metrics row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              {[
                { label: 'Profile views',    value: discovery.views.toLocaleString(),         sub: `${discovery.uniqueViewers} unique visitors` },
                { label: 'Menu browses',     value: discovery.dishAdds.toLocaleString(),       sub: `${discovery.views > 0 ? ((discovery.dishAdds / discovery.views) * 100).toFixed(0) : 0}% of viewers` },
                { label: 'Orders placed',    value: discovery.ordersPlaced.toLocaleString(),   sub: `from discovery` },
                { label: 'Conversion rate',  value: `${discovery.conversionRate}%`,            sub: `view → order`, accent: true },
              ].map(({ label, value, sub, accent }) => (
                <div key={label} style={{ background: 'var(--cream)', borderRadius: 10, padding: '1rem' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>{label}</div>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.6rem', fontWeight: 900, color: accent ? 'var(--orange)' : 'var(--ink)', lineHeight: 1 }}>{value}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.3rem' }}>{sub}</div>
                </div>
              ))}
            </div>

            {/* Conversion funnel */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1rem' }}>
                  Conversion funnel
                </div>
                <FunnelRow label="Profile views" value={discovery.views} max={discovery.views} color="#E85D04" />
                <FunnelRow label="Menu browses"  value={discovery.dishAdds} max={discovery.views} color="#F4A261"
                  pct={discovery.views > 0 ? ((discovery.dishAdds / discovery.views) * 100).toFixed(0) : 0} />
                <FunnelRow label="Orders placed" value={discovery.ordersPlaced} max={discovery.views} color="#0A6847"
                  pct={discovery.views > 0 ? ((discovery.ordersPlaced / discovery.views) * 100).toFixed(0) : 0} />
                {discovery.bounces > 0 && (
                  <FunnelRow label="Bounced" value={discovery.bounces} max={discovery.views} color="#D3D1C7"
                    sub={`${discovery.bounceRate}% bounce rate`} />
                )}
              </div>

              {/* Views over time — all tiers */}
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.8rem' }}>
                  Views over time
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={discoveryByDay} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Line type="monotone" dataKey="views"     name="Views"      stroke="#E85D04" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="dish_adds" name="Menu views" stroke="#F4A261" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="orders"    name="Orders"     stroke="#0A6847" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* ── REVENUE + ORDERS + RESERVATIONS + FULFILLMENT CHARTS — all tiers ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <Card>
              <SectionTitle>Daily revenue</SectionTitle>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={revenueByDay} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} />
                  <Tooltip content={<ChartTooltip money />} />
                  <Bar dataKey="revenue" name="Revenue" fill="#E85D04" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <SectionTitle>Daily orders</SectionTitle>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={revenueByDay} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="orders" name="Orders" fill="#0A6847" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <Card>
              <SectionTitle>Reservations over time</SectionTitle>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={resByDay} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line type="monotone" dataKey="reservations" name="Reservations" stroke="#E85D04" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="covers" name="Covers" stroke="#0A6847" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
            <Card>
              <SectionTitle>Fulfillment breakdown</SectionTitle>
              {fulfillmentData.length === 0 ? (
                <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No data yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', paddingTop: '0.5rem' }}>
                  {fulfillmentData.map(({ name, value }) => {
                    const pct = Math.round((value / orders.length) * 100);
                    return (
                      <div key={name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.3rem' }}>
                          <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{name}</span>
                          <span style={{ color: 'var(--muted)' }}>{value} orders ({pct}%)</span>
                        </div>
                        <div style={{ height: 8, background: 'var(--warm)', borderRadius: 4 }}>
                          <div style={{ height: 8, background: 'var(--orange)', borderRadius: 4, width: `${pct}%` }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>

      {/* ── BEST DAY + TOP DISHES BY REVENUE — all tiers ─────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <Card>
          <SectionTitle>Revenue by day of week</SectionTitle>
          {maxRevDay?.revenue > 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '0.8rem' }}>
              Best day: <strong style={{ color: 'var(--orange)' }}>{maxRevDay.day}</strong> — {fmtMoney(maxRevDay.revenue)} total
            </p>
          )}
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={byDayOfWeek} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'var(--muted)' }} />
              <YAxis tick={{ fontSize: 10, fill: 'var(--muted)' }} />
              <Tooltip content={<ChartTooltip money />} />
              <Bar dataKey="revenue" name="Revenue" fill="#E85D04" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <SectionTitle>Top dishes by revenue</SectionTitle>
          {topDishes.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No data yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
              {[...topDishes].sort((a, b) => b.revenue - a.revenue).slice(0, 6).map((dish, i) => {
                const maxRev = [...topDishes].sort((a, b) => b.revenue - a.revenue)[0].revenue;
                return (
                  <div key={dish.name} style={{ display: 'grid', gridTemplateColumns: '20px 1fr 80px', gap: '0.6rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)' }}>#{i + 1}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', marginBottom: 3 }}>{dish.name}</div>
                      <div style={{ height: 3, background: 'var(--warm)', borderRadius: 2 }}>
                        <div style={{ height: 3, background: 'var(--orange)', borderRadius: 2, width: `${(dish.revenue / maxRev) * 100}%` }}/>
                      </div>
                    </div>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--orange)', textAlign: 'right' }}>{fmtMoney(dish.revenue)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ── DISH INTELLIGENCE — all tiers, built from verified orders + reviews ──── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <Card>
          <SectionTitle>Dish intelligence</SectionTitle>
          <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '-0.8rem', marginBottom: '1.2rem' }}>
            What customers love and what needs attention, per dish — based on verified orders and reviews, not just sales volume.
          </p>
          {dishIntelLoading ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Loading...</p>
          ) : dishIntel.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No dish data yet.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
              {dishIntel.slice(0, 6).map(dish => (
                <div key={dish.dishId || dish.dishName} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '1rem 1.1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.3rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{dish.dishName}</div>
                    {dish.avgRating != null && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{dish.avgRating.toFixed(1)} ★</div>
                    )}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.7rem' }}>
                    {dish.verifiedOrderCount} verified order{dish.verifiedOrderCount === 1 ? '' : 's'} · {dish.reviewCount} review{dish.reviewCount === 1 ? '' : 's'}
                  </div>

                  {dish.whatCustomersLove.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#27500A', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                        What customers love
                      </div>
                      {dish.whatCustomersLove.slice(0, 3).map(t => (
                        <div key={t.topic} style={{ fontSize: '0.78rem', color: 'var(--ink)', display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ textTransform: 'capitalize' }}>{t.topic.replace('_', ' ')}</span>
                          <span style={{ color: '#27500A', fontWeight: 600 }}>{t.pctPositive}%</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {dish.whatNeedsAttention.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#791F1F', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                        Needs attention
                      </div>
                      {dish.whatNeedsAttention.slice(0, 3).map(t => (
                        <div key={t.topic} style={{ fontSize: '0.78rem', color: 'var(--ink)', display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ textTransform: 'capitalize' }}>{t.topic.replace('_', ' ')}</span>
                          <span style={{ color: '#791F1F', fontWeight: 600 }}>{100 - t.pctPositive}% negative</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ── TRAFFIC SOURCE ATTRIBUTION ────────────────────────────────────────── */}
      {(() => {
        const orderSourceMap = {};
        orders.forEach(o => {
          const src = o.order_source || 'marketplace';
          const utmSrc = o.utm_params?.utm_source;
          const label = src === 'utm' && utmSrc ? `utm / ${utmSrc}` : src;
          if (!orderSourceMap[label]) orderSourceMap[label] = { count: 0, revenue: 0 };
          orderSourceMap[label].count++;
          orderSourceMap[label].revenue += o.total || 0;
        });

        const resSourceMap = {};
        reservations.forEach(r => {
          const src = r.reservation_source || 'marketplace';
          const utmSrc = r.utm_params?.utm_source;
          const label = src === 'utm' && utmSrc ? `utm / ${utmSrc}` : src;
          if (!resSourceMap[label]) resSourceMap[label] = { count: 0 };
          resSourceMap[label].count++;
        });

        const orderSources = Object.entries(orderSourceMap).sort((a, b) => b[1].revenue - a[1].revenue);
        const resSources   = Object.entries(resSourceMap).sort((a, b) => b[1].count - a[1].count);
        if (orderSources.length === 0 && resSources.length === 0) return null;

        return (
          <Card style={{ marginBottom: '1.5rem' }}>
            <SectionTitle>Traffic source attribution</SectionTitle>
            {orderSources.length > 0 && (
              <>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '1rem 0 0.6rem' }}>Orders</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {orderSources.map(([src, data]) => {
                    const pct = orders.length > 0 ? Math.round((data.count / orders.length) * 100) : 0;
                    return (
                      <div key={src} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 80px 80px', gap: '1rem', alignItems: 'center' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, textTransform: 'capitalize' }}>{src}</div>
                        <div style={{ background: 'var(--warm)', borderRadius: 20, height: 8, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--orange)', borderRadius: 20 }} />
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--muted)', textAlign: 'right' }}>{data.count} orders</div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--orange)', textAlign: 'right' }}>{fmtMoney(data.revenue)}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {resSources.length > 0 && (
              <>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '1.2rem 0 0.6rem' }}>Reservations</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {resSources.map(([src, data]) => {
                    const pct = reservations.length > 0 ? Math.round((data.count / reservations.length) * 100) : 0;
                    return (
                      <div key={src} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 80px', gap: '1rem', alignItems: 'center' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, textTransform: 'capitalize' }}>{src}</div>
                        <div style={{ background: 'var(--warm)', borderRadius: 20, height: 8, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: '#0A6847', borderRadius: 20 }} />
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--muted)', textAlign: 'right' }}>{data.count} reservations</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Card>
        );
      })()}
    </div>
  );
}
